import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api";
import {
  trackLeadCreated, trackLeadStageChanged, trackLeadActivityLogged,
  trackLeadDeleted, trackLeadPipeline,
} from "@/lib/metrics";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Plus, Search, LayoutGrid, List, Phone, Mail, Building2,
  Globe, Calendar, DollarSign, ChevronRight, X, Edit2,
  CheckCircle2, Clock, TrendingUp, SlidersHorizontal,
  MessageSquare, User, ArrowRight, Trash2, Linkedin,
  ChevronLeft, ChevronRight as ChevronRightIcon, Star,
  Zap, Coffee, Video, FileText, AlertTriangle, Activity,
  MousePointerClick, ChevronDown, ChevronUp, ExternalLink,
  Flag, ArrowUpRight, Tag, Hash, Send,
  MessageCircle, Bell, Flame, Sparkles, CheckSquare,
  Presentation, ThumbsUp, Timer, Lightbulb, Eye
} from "lucide-react";

type Lead = {
  id: string; email: string; name: string; phone: string; country: string;
  companyName: string; companyId: string | null; stage: string; source: string;
  dealValue: number | null; notes: string; status: string; demoCount: number;
  lastContactedAt: string | null; nextFollowUpAt: string | null;
  createdAt: string; confirmedAt?: string | null; convertedAt: string | null;
  expiresAt: string | null; priority: string; assignedTo: string | null;
  website: string; linkedinUrl: string;
};

type Activity = {
  id: string; leadId: string; type: string; content: string;
  meta?: Record<string, unknown> | null; createdAt: string;
};

const STAGES = [
  { id: "new",            label: "New Lead",       color: "bg-slate-500",    text: "text-slate-700",  border: "border-slate-300",    bg: "bg-slate-50",    dot: "bg-slate-500",   ring: "ring-slate-200" },
  { id: "contacted",      label: "Contacted",      color: "bg-blue-600",     text: "text-blue-700",   border: "border-blue-200",     bg: "bg-blue-50",     dot: "bg-blue-500",    ring: "ring-blue-200"  },
  { id: "demo_scheduled", label: "Demo Scheduled", color: "bg-violet-600",   text: "text-violet-700", border: "border-violet-200",   bg: "bg-violet-50",   dot: "bg-violet-500",  ring: "ring-violet-200"},
  { id: "proposal",       label: "Proposal Sent",  color: "bg-amber-600",    text: "text-amber-700",  border: "border-amber-200",    bg: "bg-amber-50",    dot: "bg-amber-500",   ring: "ring-amber-200" },
  { id: "negotiation",    label: "Negotiation",    color: "bg-orange-600",   text: "text-orange-700", border: "border-orange-200",   bg: "bg-orange-50",   dot: "bg-orange-500",  ring: "ring-orange-200"},
  { id: "won",            label: "Closed Won",     color: "bg-emerald-600",  text: "text-emerald-700",border: "border-emerald-200",  bg: "bg-emerald-50",  dot: "bg-emerald-500", ring: "ring-emerald-200"},
  { id: "lost",           label: "Closed Lost",    color: "bg-rose-600",     text: "text-rose-700",   border: "border-rose-200",     bg: "bg-rose-50",     dot: "bg-rose-500",    ring: "ring-rose-200"  },
];

const PIPELINE_STAGES = ["new", "contacted", "demo_scheduled", "proposal", "negotiation"];

const SOURCES = [
  { id: "demo_request", label: "Demo Request" }, { id: "manual", label: "Manual Entry" },
  { id: "referral", label: "Referral" }, { id: "website", label: "Website" },
  { id: "linkedin", label: "LinkedIn" }, { id: "email", label: "Email Campaign" },
  { id: "other", label: "Other" },
];

const ACTIVITY_TYPES = [
  { id: "call",          label: "Call",        icon: Phone,          color: "text-blue-500",   bg: "bg-blue-50",    dot: "bg-blue-500"    },
  { id: "email",         label: "Email",       icon: Mail,           color: "text-violet-500", bg: "bg-violet-50",  dot: "bg-violet-500"  },
  { id: "whatsapp",      label: "WhatsApp",    icon: MessageCircle,  color: "text-green-600",  bg: "bg-green-50",   dot: "bg-green-500"   },
  { id: "meeting",       label: "Meeting",     icon: Video,          color: "text-amber-500",  bg: "bg-amber-50",   dot: "bg-amber-500"   },
  { id: "demo_given",    label: "Demo Given",  icon: Presentation,   color: "text-indigo-500", bg: "bg-indigo-50",  dot: "bg-indigo-500"  },
  { id: "proposal_sent", label: "Proposal",    icon: FileText,       color: "text-orange-500", bg: "bg-orange-50",  dot: "bg-orange-500"  },
  { id: "task",          label: "Task",        icon: CheckSquare,    color: "text-purple-500", bg: "bg-purple-50",  dot: "bg-purple-500"  },
  { id: "note",          label: "Note",        icon: Bell,           color: "text-gray-500",   bg: "bg-gray-50",    dot: "bg-gray-400"    },
  { id: "stage_change",  label: "Stage",       icon: ArrowUpRight,   color: "text-emerald-500",bg: "bg-emerald-50", dot: "bg-emerald-500" },
];

const EMAIL_TEMPLATES = [
  { label: "Intro",         text: "Hi [Name], I wanted to reach out about FinProject — a construction financial management platform that helps teams track budgets, invoices, and cash flow in one place. Would you be open to a quick 15-minute call to see if it could help your team?" },
  { label: "Demo Follow-up", text: "Hi [Name], thanks for joining the demo! I hope it gave you a clear picture of how FinProject can simplify your financial operations. Do you have any questions, or would you like to discuss pricing and next steps?" },
  { label: "Proposal Check", text: "Hi [Name], I wanted to follow up on the proposal I sent over. Have you had a chance to review it? I'm happy to walk through any questions or adjust anything to better fit your needs." },
  { label: "Re-engage",     text: "Hi [Name], I know things get busy — just wanted to check in and see if financial management for your construction projects is still a priority. We've made some improvements since we last spoke that might be relevant to you." },
];

const STAGE_NEXT_STEPS: Record<string, { icon: React.ElementType; color: string; bg: string; text: string; action: string }> = {
  new:            { icon: Mail,         color: "text-blue-600",    bg: "bg-blue-50",    text: "Send an intro email to break the ice and qualify the lead.",            action: "Log Email" },
  contacted:      { icon: Presentation, color: "text-indigo-600",  bg: "bg-indigo-50",  text: "Schedule a product demo to show the platform in action.",                action: "Log Call"  },
  demo_scheduled: { icon: FileText,     color: "text-amber-600",   bg: "bg-amber-50",   text: "Prepare demo materials and confirm date/time with the lead.",            action: "Log Call"  },
  proposal:       { icon: Timer,        color: "text-orange-600",  bg: "bg-orange-50",  text: "Follow up on the proposal — leads go cold after 3 days of silence.",     action: "Log Email" },
  negotiation:    { icon: Lightbulb,    color: "text-violet-600",  bg: "bg-violet-50",  text: "Offer a revised proposal, a discount, or a call to address objections.",  action: "Log Call"  },
  won:            { icon: ThumbsUp,     color: "text-emerald-600", bg: "bg-emerald-50", text: "Onboard the client and set up their account. Celebrate the win!",         action: "Log Note"  },
  lost:           { icon: Eye,          color: "text-gray-500",    bg: "bg-gray-50",    text: "Add to a nurture sequence. Revisit in 3 months — priorities change.",     action: "Log Note"  },
};

function leadScore(lead: Lead, activityCount: number): { score: number; label: string; color: string; bg: string; dot: string } {
  let score = 0;
  // Stage progress
  const stageScores: Record<string, number> = { new: 5, contacted: 15, demo_scheduled: 30, proposal: 45, negotiation: 60, won: 100, lost: 0 };
  score += stageScores[lead.stage] ?? 5;
  // Activity volume
  score += Math.min(activityCount * 4, 20);
  // Deal value
  const dv = lead.dealValue ?? 0;
  if (dv > 50000) score += 15; else if (dv > 10000) score += 10; else if (dv > 1000) score += 5;
  // Recency of contact
  if (lead.lastContactedAt) {
    const daysAgoContact = (Date.now() - new Date(lead.lastContactedAt).getTime()) / 86400000;
    if (daysAgoContact < 2) score += 10; else if (daysAgoContact < 7) score += 5;
  }
  // Priority
  if (lead.priority === "high") score += 10;
  // Clamp
  score = Math.min(score, 100);
  if (score >= 65) return { score, label: "Hot",  color: "text-rose-600",   bg: "bg-rose-50",   dot: "bg-rose-500"  };
  if (score >= 35) return { score, label: "Warm", color: "text-amber-600",  bg: "bg-amber-50",  dot: "bg-amber-500" };
  return               { score, label: "Cold", color: "text-slate-500",  bg: "bg-slate-50",  dot: "bg-slate-400" };
}

function needsAttention(lead: Lead): boolean {
  if (lead.stage === "won" || lead.stage === "lost") return false;
  if (isOverdue(lead.nextFollowUpAt)) return true;
  if (lead.lastContactedAt) {
    const days = (Date.now() - new Date(lead.lastContactedAt).getTime()) / 86400000;
    if (days > 7) return true;
  } else {
    const days = (Date.now() - new Date(lead.createdAt).getTime()) / 86400000;
    if (days > 3) return true;
  }
  return false;
}

const PRIORITIES = [
  { id: "high",   label: "High",   color: "text-rose-600",   bg: "bg-rose-50",   border: "border-rose-200",   dot: "bg-rose-500"   },
  { id: "normal", label: "Normal", color: "text-blue-600",   bg: "bg-blue-50",   border: "border-blue-200",   dot: "bg-blue-500"   },
  { id: "low",    label: "Low",    color: "text-gray-500",   bg: "bg-gray-100",  border: "border-gray-200",   dot: "bg-gray-400"   },
];

function getStage(id: string)    { return STAGES.find(s => s.id === id) ?? STAGES[0]; }
function getSource(id: string)   { return SOURCES.find(s => s.id === id)?.label ?? id; }
function getPriority(id: string) { return PRIORITIES.find(p => p.id === id) ?? PRIORITIES[1]; }
function getActivity(id: string) { return ACTIVITY_TYPES.find(a => a.id === id) ?? ACTIVITY_TYPES[3]; }

function avatar(lead: Lead) {
  return (lead.name ? lead.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) : lead.email[0].toUpperCase());
}

function fmtDate(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateTime(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function fmtCurrency(v: number | null) {
  if (v == null) return "—";
  return "$" + v.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function daysAgo(d: string | null | undefined) {
  if (!d) return null;
  const diff = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return `${diff}d ago`;
}

function isOverdue(d: string | null | undefined) {
  if (!d) return false;
  return new Date(d) < new Date();
}

function StatCard({ label, value, sub, icon: Icon, color, onClick, active }: {
  label: string; value: string | number; sub?: string;
  icon: React.ElementType; color: string; onClick?: () => void; active?: boolean;
}) {
  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-2xl p-5 flex items-start gap-4 border shadow-sm transition-all ${
        onClick ? "cursor-pointer hover:border-blue-300 hover:shadow-md" : ""
      } ${active ? "border-blue-400 ring-2 ring-blue-100" : "border-gray-200"}`}
    >
      <div className={`w-9 h-9 rounded-xl ${color} flex items-center justify-center shrink-0`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div>
        <div className="text-xl font-bold text-gray-900">{value}</div>
        <div className="text-xs text-gray-500 mt-0.5">{label}</div>
        {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function StageBadge({ stage }: { stage: string }) {
  const s = getStage(stage);
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${s.text} ${s.bg} border ${s.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function PriorityBadge({ priority }: { priority: string }) {
  const p = getPriority(priority);
  if (priority === "normal") return null;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${p.color} ${p.bg} border ${p.border}`}>
      <Flag className="w-2.5 h-2.5" />
      {p.label}
    </span>
  );
}

function LeadCard({ lead, onClick, onStageChange }: {
  lead: Lead; onClick: () => void;
  onStageChange: (id: string, stage: string) => void;
}) {
  const s = getStage(lead.stage);
  const stageIdx = STAGES.findIndex(st => st.id === lead.stage);
  const overdue = isOverdue(lead.nextFollowUpAt);
  const age = daysAgo(lead.createdAt);
  const p = getPriority(lead.priority ?? "normal");

  const handleAdvance = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (stageIdx < STAGES.length - 1) onStageChange(lead.id, STAGES[stageIdx + 1].id);
  };

  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl p-3.5 border cursor-pointer hover:shadow-md transition-all group shadow-sm ${
        lead.priority === "high" ? "border-rose-200 ring-1 ring-rose-100" : "border-gray-200 hover:border-gray-300"
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-7 h-7 rounded-full ${s.color} flex items-center justify-center text-white text-xs font-bold shrink-0`}>
            {avatar(lead)}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate leading-tight">{lead.name || lead.email}</div>
            {lead.name && <div className="text-xs text-gray-400 truncate">{lead.email}</div>}
          </div>
        </div>
        {lead.priority === "high" && <Flag className="w-3 h-3 text-rose-500 shrink-0 mt-0.5" />}
      </div>

      {lead.companyName && (
        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
          <Building2 className="w-3 h-3 shrink-0 text-gray-400" />
          <span className="truncate">{lead.companyName}</span>
        </div>
      )}

      <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-gray-100">
        <span className="text-xs text-gray-400">{age}</span>
        {lead.dealValue ? (
          <span className="text-xs font-semibold text-emerald-700">{fmtCurrency(lead.dealValue)}</span>
        ) : (
          <span className="text-xs text-gray-300">—</span>
        )}
      </div>

      {overdue && lead.nextFollowUpAt && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-rose-600 bg-rose-50 px-2 py-1 rounded-lg">
          <AlertTriangle className="w-3 h-3 shrink-0" />
          Overdue follow-up {fmtDate(lead.nextFollowUpAt)}
        </div>
      )}
      {!overdue && lead.nextFollowUpAt && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600">
          <Calendar className="w-3 h-3" />
          Follow-up {fmtDate(lead.nextFollowUpAt)}
        </div>
      )}

      <div className="mt-2 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="text-xs text-gray-400">{getSource(lead.source)}</span>
        {stageIdx < STAGES.length - 1 && stageIdx >= 0 && (
          <button
            onClick={handleAdvance}
            className="text-xs flex items-center gap-1 text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-2 py-0.5 rounded-lg transition-colors"
          >
            Move <ChevronRightIcon className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function AddLeadModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [error, setError] = useState("");
  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiPost("/cp/leads", data),
    onSuccess: (_, vars) => {
      trackLeadCreated(vars.dealValue as number | null);
      qc.invalidateQueries({ queryKey: ["cp-leads"] });
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault(); setError("");
    const fd = new FormData(e.currentTarget);
    const dealRaw = fd.get("dealValue") as string;
    mutation.mutate({
      email: fd.get("email"), name: fd.get("name"), phone: fd.get("phone"),
      country: fd.get("country"), companyName: fd.get("companyName"),
      source: fd.get("source"), stage: fd.get("stage"),
      priority: fd.get("priority"),
      dealValue: dealRaw ? parseFloat(dealRaw) : null,
      notes: fd.get("notes"), website: fd.get("website"), linkedinUrl: fd.get("linkedinUrl"),
    });
  };
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-bold text-lg">Add Lead</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Full Name</Label><Input name="name" className="rounded-xl" placeholder="Alex Johnson" /></div>
            <div className="space-y-1.5"><Label>Email *</Label><Input name="email" type="email" required className="rounded-xl" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Phone</Label><Input name="phone" className="rounded-xl" /></div>
            <div className="space-y-1.5"><Label>Country</Label><Input name="country" className="rounded-xl" /></div>
          </div>
          <div className="space-y-1.5"><Label>Company Name</Label><Input name="companyName" className="rounded-xl" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Website</Label><Input name="website" className="rounded-xl" placeholder="https://" /></div>
            <div className="space-y-1.5"><Label>LinkedIn URL</Label><Input name="linkedinUrl" className="rounded-xl" placeholder="linkedin.com/in/..." /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Source</Label>
              <Select name="source" defaultValue="manual">
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Stage</Label>
              <Select name="stage" defaultValue="new">
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select name="priority" defaultValue="normal">
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Deal Value ($)</Label><Input name="dealValue" type="number" min="0" className="rounded-xl" /></div>
          <div className="space-y-1.5"><Label>Notes</Label><Textarea name="notes" className="rounded-xl resize-none" rows={3} /></div>
          {error && <p className="text-rose-500 text-sm">{error}</p>}
          <div className="flex gap-3 pt-1">
            <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-700 text-white" disabled={mutation.isPending}>
              {mutation.isPending ? "Saving..." : "Add Lead"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ActivityFeed({ lead, activities, onActivityAdded, onFollowUpSet }: {
  lead: Lead; activities: Activity[];
  onActivityAdded: () => void;
  onFollowUpSet?: (date: string) => void;
}) {
  const qc = useQueryClient();
  const [logType, setLogType] = useState<string | null>(null);
  const [content, setContent] = useState("");
  const textRef = useRef<HTMLTextAreaElement>(null);

  const addActivity = useMutation({
    mutationFn: (data: { type: string; content: string }) =>
      apiPost(`/cp/leads/${lead.id}/activities`, data),
    onSuccess: (_, vars) => {
      trackLeadActivityLogged(vars.type);
      qc.invalidateQueries({ queryKey: ["lead-activities", lead.id] });
      setContent(""); setLogType(null); onActivityAdded();
    },
  });

  const handleLog = () => {
    if (!content.trim() || !logType) return;
    addActivity.mutate({ type: logType, content: content.trim() });
  };

  const setFollowUp = (days: number) => {
    const d = new Date(); d.setDate(d.getDate() + days);
    onFollowUpSet?.(d.toISOString().slice(0, 10));
  };

  const ROW1 = [
    { type: "call",         icon: Phone,          label: "Call",     color: "text-blue-600 hover:bg-blue-50 border-blue-200"     },
    { type: "email",        icon: Mail,            label: "Email",    color: "text-violet-600 hover:bg-violet-50 border-violet-200"},
    { type: "whatsapp",     icon: MessageCircle,   label: "WhatsApp", color: "text-green-600 hover:bg-green-50 border-green-200"   },
    { type: "meeting",      icon: Video,           label: "Meeting",  color: "text-amber-600 hover:bg-amber-50 border-amber-200"   },
  ];
  const ROW2 = [
    { type: "demo_given",    icon: Presentation, label: "Demo Given",  color: "text-indigo-600 hover:bg-indigo-50 border-indigo-200" },
    { type: "proposal_sent", icon: FileText,     label: "Proposal",    color: "text-orange-600 hover:bg-orange-50 border-orange-200" },
    { type: "task",          icon: CheckSquare,  label: "Task",        color: "text-purple-600 hover:bg-purple-50 border-purple-200" },
    { type: "note",          icon: Bell,         label: "Note",        color: "text-gray-600 hover:bg-gray-100 border-gray-200"      },
  ];

  const at = logType ? getActivity(logType) : null;

  return (
    <div className="space-y-4">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
        <Activity className="w-3.5 h-3.5" /> Activity Log
      </div>

      {/* Quick action buttons — two rows */}
      <div className="space-y-1.5">
        {[ROW1, ROW2].map((row, ri) => (
          <div key={ri} className="flex gap-1.5 flex-wrap">
            {row.map(qa => (
              <button
                key={qa.type}
                onClick={() => { setLogType(qa.type); setTimeout(() => textRef.current?.focus(), 50); }}
                className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-xl border font-medium transition-all bg-white ${qa.color} ${logType === qa.type ? "ring-2 ring-offset-1 ring-blue-300 shadow-sm" : ""}`}
              >
                <qa.icon className="w-3 h-3" />
                {qa.label}
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* Follow-up shortcuts */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-xs text-gray-400 shrink-0">Follow-up in:</span>
        {[
          { label: "+1d",  days: 1  },
          { label: "+3d",  days: 3  },
          { label: "+1w",  days: 7  },
          { label: "+2w",  days: 14 },
          { label: "+1m",  days: 30 },
        ].map(s => (
          <button
            key={s.days}
            onClick={() => setFollowUp(s.days)}
            className="text-xs px-2 py-0.5 rounded-lg border border-gray-200 text-gray-500 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50 transition-all font-mono"
          >
            {s.label}
          </button>
        ))}
        {lead.nextFollowUpAt && (
          <span className={`text-xs ml-1 font-medium ${isOverdue(lead.nextFollowUpAt) ? "text-rose-600" : "text-amber-600"}`}>
            → {fmtDate(lead.nextFollowUpAt)}{isOverdue(lead.nextFollowUpAt) ? " ⚠" : ""}
          </span>
        )}
      </div>

      {/* Activity compose area */}
      {logType && at && (
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-3 space-y-2.5">
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-700">
            <at.icon className={`w-3.5 h-3.5 ${at.color}`} />
            {at.label}
          </div>

          {/* Email templates */}
          {logType === "email" && (
            <div className="flex gap-1.5 flex-wrap">
              <span className="text-xs text-gray-400 self-center">Templates:</span>
              {EMAIL_TEMPLATES.map(t => (
                <button
                  key={t.label}
                  onClick={() => setContent(t.text.replace("[Name]", lead.name?.split(" ")[0] || "there"))}
                  className="text-xs px-2 py-0.5 rounded-lg border border-violet-200 text-violet-600 hover:bg-violet-50 transition-colors"
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {/* WhatsApp direct link */}
          {logType === "whatsapp" && lead.phone && (
            <a
              href={`https://wa.me/${lead.phone.replace(/\D/g, "")}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors"
            >
              <MessageCircle className="w-3 h-3" />
              Open WhatsApp for {lead.phone}
            </a>
          )}

          <Textarea
            ref={textRef}
            value={content}
            onChange={e => setContent(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && e.metaKey) handleLog(); }}
            className="rounded-xl resize-none text-sm bg-white"
            rows={3}
            placeholder={
              logType === "task" ? "Describe the task... (what, who, when)"
              : logType === "email" ? "Summary of email or paste message..."
              : logType === "whatsapp" ? "Summary of conversation..."
              : `Notes about this ${at.label.toLowerCase()}... (⌘Enter to save)`
            }
          />
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" className="text-xs rounded-xl h-7" onClick={() => { setLogType(null); setContent(""); }}>Cancel</Button>
            <Button
              size="sm"
              className="text-xs rounded-xl h-7 bg-blue-600 hover:bg-blue-700 text-white"
              onClick={handleLog}
              disabled={!content.trim() || addActivity.isPending}
            >
              <Send className="w-3 h-3 mr-1" />
              {addActivity.isPending ? "Saving..." : "Log"}
            </Button>
          </div>
        </div>
      )}

      {/* Activity timeline */}
      <div className="space-y-0">
        {activities.length === 0 && (
          <div className="text-center py-6 text-xs text-gray-400">
            <Activity className="w-5 h-5 mx-auto mb-2 opacity-30" />
            No activities yet. Log a call, email, or WhatsApp above.
          </div>
        )}
        {activities.map((act, idx) => {
          const aType = getActivity(act.type);
          const Icon = aType.icon;
          const isLast = idx === activities.length - 1;
          return (
            <div key={act.id} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className={`w-7 h-7 rounded-full ${aType.bg} flex items-center justify-center shrink-0`}>
                  <Icon className={`w-3.5 h-3.5 ${aType.color}`} />
                </div>
                {!isLast && <div className="w-px flex-1 bg-gray-100 my-1" />}
              </div>
              <div className="pb-4 min-w-0 flex-1">
                <div className="flex items-start gap-2">
                  <span className={`text-xs font-semibold shrink-0 ${aType.color}`}>{aType.label}</span>
                </div>
                <p className="text-sm text-gray-700 leading-snug break-words mt-0.5">{act.content}</p>
                <p className="text-xs text-gray-400 mt-0.5">{fmtDateTime(act.createdAt)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StageStepper({ lead, onStageChange, saving }: {
  lead: Lead; onStageChange: (s: string) => void; saving: boolean;
}) {
  const currentIdx = STAGES.findIndex(s => s.id === lead.stage);
  const pipelineIdx = PIPELINE_STAGES.findIndex(s => s === lead.stage);
  const inPipeline = pipelineIdx >= 0;

  return (
    <div className="space-y-3">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pipeline Stage</div>

      {inPipeline && (
        <div className="flex items-center gap-1">
          {PIPELINE_STAGES.map((sid, idx) => {
            const s = getStage(sid);
            const done = idx <= pipelineIdx;
            return (
              <div key={sid} className="flex items-center gap-1 flex-1">
                <button
                  onClick={() => !saving && onStageChange(sid)}
                  className={`h-1.5 flex-1 rounded-full transition-all ${done ? s.dot : "bg-gray-200"} ${saving ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:opacity-80"}`}
                  title={s.label}
                />
              </div>
            );
          })}
        </div>
      )}

      <div className="flex gap-2">
        <Select value={lead.stage} onValueChange={onStageChange} disabled={saving}>
          <SelectTrigger className="rounded-xl flex-1">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${getStage(lead.stage).dot}`} />
              <SelectValue />
            </div>
          </SelectTrigger>
          <SelectContent>
            {STAGES.map(st => (
              <SelectItem key={st.id} value={st.id}>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                  {st.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {currentIdx > 0 && (
          <Button size="sm" variant="outline" className="rounded-xl px-3" disabled={saving}
            onClick={() => onStageChange(STAGES[currentIdx - 1].id)} title="Move back">
            <ChevronLeft className="w-3.5 h-3.5" />
          </Button>
        )}
        {currentIdx < STAGES.length - 1 && (
          <Button size="sm" className="rounded-xl px-3 bg-blue-600 hover:bg-blue-700 text-white" disabled={saving}
            onClick={() => onStageChange(STAGES[currentIdx + 1].id)} title="Move forward">
            <ChevronRightIcon className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

function LeadDetailPanel({ lead, onClose, onUpdate, onDelete }: {
  lead: Lead; onClose: () => void;
  onUpdate: (patch: Record<string, unknown>) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<"details" | "activity" | "notes">("activity");

  const { data: activities = [], refetch: refetchActivities } = useQuery<Activity[]>({
    queryKey: ["lead-activities", lead.id],
    queryFn: () => apiGet(`/cp/leads/${lead.id}/activities`),
  });

  const s = getStage(lead.stage);
  const overdue = isOverdue(lead.nextFollowUpAt);
  const p = getPriority(lead.priority ?? "normal");

  const handleStageChange = async (stage: string) => {
    setSaving(true);
    trackLeadStageChanged(lead.stage, stage);
    await onUpdate({ stage });
    setSaving(false);
  };

  const handlePriorityChange = async (priority: string) => {
    setSaving(true);
    await onUpdate({ priority });
    setSaving(false);
  };

  const handleMarkContacted = async () => {
    setSaving(true);
    await onUpdate({ lastContactedAt: new Date().toISOString() });
    setSaving(false);
  };

  const handleScheduleFollowUp = async (date: string) => {
    setSaving(true);
    await onUpdate({ nextFollowUpAt: date });
    setSaving(false);
  };

  const handleEditSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault(); setSaving(true);
    const fd = new FormData(e.currentTarget);
    const dealRaw = fd.get("dealValue") as string;
    await onUpdate({
      name: fd.get("name"), phone: fd.get("phone"), country: fd.get("country"),
      companyName: fd.get("companyName"), source: fd.get("source"),
      dealValue: dealRaw ? parseFloat(dealRaw) : null,
      nextFollowUpAt: (fd.get("nextFollowUpAt") as string) || null,
      website: fd.get("website"), linkedinUrl: fd.get("linkedinUrl"),
      assignedTo: fd.get("assignedTo") || null,
    });
    setSaving(false); setEditMode(false);
  };

  const handleSaveNotes = async (notes: string) => {
    setSaving(true);
    await onUpdate({ notes });
    setSaving(false);
  };

  const tabs = [
    { id: "activity", label: "Activity", count: activities.length },
    { id: "details",  label: "Details",  count: null },
    { id: "notes",    label: "Notes",    count: null },
  ] as const;

  return (
    <div className="fixed inset-y-0 right-0 w-[480px] bg-white border-l border-gray-200 z-50 flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">
      <div className={`px-6 py-4 border-b border-gray-200 ${s.bg}`}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-full ${s.color} flex items-center justify-center text-white text-sm font-bold shrink-0`}>
              {avatar(lead)}
            </div>
            <div>
              <div className="font-bold text-gray-900 leading-tight">{lead.name || lead.email}</div>
              {lead.name && <div className="text-xs text-gray-500">{lead.email}</div>}
              {lead.companyName && (
                <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                  <Building2 className="w-3 h-3" />
                  {lead.companyName}
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition-colors p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <StageBadge stage={lead.stage} />
          <PriorityBadge priority={lead.priority ?? "normal"} />
          {(() => {
            const sc = leadScore(lead, 0);
            return (
              <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-semibold ${sc.color} ${sc.bg}`}>
                <Flame className="w-2.5 h-2.5" /> {sc.label}
              </span>
            );
          })()}
          {lead.dealValue != null && (
            <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
              {fmtCurrency(lead.dealValue)}
            </span>
          )}
          {overdue && lead.nextFollowUpAt && (
            <span className="text-xs text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full border border-rose-200 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Overdue
            </span>
          )}
        </div>

        <div className="flex items-center gap-3 mt-3">
          {lead.email && (
            <a href={`mailto:${lead.email}`} className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 transition-colors">
              <Mail className="w-3.5 h-3.5" /> Email
            </a>
          )}
          {lead.phone && (
            <a href={`tel:${lead.phone}`} className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 transition-colors">
              <Phone className="w-3.5 h-3.5" /> Call
            </a>
          )}
          {lead.website && (
            <a href={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 transition-colors">
              <Globe className="w-3.5 h-3.5" /> Web
            </a>
          )}
          {lead.linkedinUrl && (
            <a href={lead.linkedinUrl.startsWith("http") ? lead.linkedinUrl : `https://${lead.linkedinUrl}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 transition-colors">
              <Linkedin className="w-3.5 h-3.5" /> LinkedIn
            </a>
          )}
        </div>
      </div>

      <div className="flex border-b border-gray-200 bg-white">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2.5 text-xs font-medium transition-colors flex items-center justify-center gap-1.5 ${
              activeTab === tab.id
                ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50/50"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
            {tab.count != null && tab.count > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-500"}`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeTab === "activity" && (
          <div className="px-6 py-5 space-y-5">
            <StageStepper lead={lead} onStageChange={handleStageChange} saving={saving} />

            {/* Next-step suggestion */}
            {(() => {
              const ns = STAGE_NEXT_STEPS[lead.stage];
              if (!ns) return null;
              const Icon = ns.icon;
              return (
                <div className={`rounded-xl border px-3.5 py-3 flex items-start gap-2.5 ${ns.bg}`}>
                  <Icon className={`w-4 h-4 ${ns.color} shrink-0 mt-0.5`} />
                  <div>
                    <div className={`text-xs font-semibold ${ns.color} mb-0.5`}>Suggested next step</div>
                    <div className="text-xs text-gray-600 leading-relaxed">{ns.text}</div>
                  </div>
                </div>
              );
            })()}

            {/* Priority + Mark Contacted */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-400">Priority:</span>
                {PRIORITIES.map(pr => (
                  <button
                    key={pr.id}
                    onClick={() => handlePriorityChange(pr.id)}
                    disabled={saving}
                    className={`text-xs px-2 py-0.5 rounded-full border font-medium transition-all ${
                      (lead.priority ?? "normal") === pr.id
                        ? `${pr.color} ${pr.bg} ${pr.border} ring-1 ring-offset-1 ring-current`
                        : "text-gray-400 bg-white border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    {pr.label}
                  </button>
                ))}
              </div>
              <Button size="sm" variant="outline" className="rounded-xl text-emerald-700 hover:bg-emerald-50 border-emerald-200 text-xs h-7 px-2.5" onClick={handleMarkContacted} disabled={saving}>
                <Phone className="w-3 h-3 mr-1" /> Contacted
              </Button>
            </div>

            <ActivityFeed
              lead={lead}
              activities={activities}
              onActivityAdded={refetchActivities}
              onFollowUpSet={handleScheduleFollowUp}
            />
          </div>
        )}

        {activeTab === "details" && (
          <div className="px-6 py-5 space-y-5">
            {!editMode ? (
              <>
                <div className="bg-gray-50 rounded-xl p-4 space-y-3 border border-gray-100">
                  {[
                    { icon: Mail,      val: lead.email,       label: "Email"    },
                    { icon: Phone,     val: lead.phone,       label: "Phone"    },
                    { icon: Building2, val: lead.companyName, label: "Company"  },
                    { icon: Globe,     val: lead.country,     label: "Country"  },
                    { icon: Tag,       val: getSource(lead.source), label: "Source" },
                    { icon: User,      val: lead.assignedTo,  label: "Assigned" },
                    { icon: ExternalLink, val: lead.website,  label: "Website"  },
                    { icon: Linkedin,  val: lead.linkedinUrl, label: "LinkedIn" },
                  ].filter(r => r.val).map(row => (
                    <div key={row.label} className="flex items-center gap-2 text-sm">
                      <row.icon className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="text-gray-500 text-xs w-16 shrink-0">{row.label}</span>
                      <span className="text-gray-800 truncate">{row.val}</span>
                    </div>
                  ))}
                  {lead.dealValue != null && (
                    <div className="flex items-center gap-2 text-sm pt-2 border-t border-gray-100">
                      <DollarSign className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="text-gray-500 text-xs w-16 shrink-0">Deal</span>
                      <span className="text-emerald-700 font-semibold">{fmtCurrency(lead.dealValue)}</span>
                    </div>
                  )}
                  {lead.nextFollowUpAt && (
                    <div className={`flex items-center gap-2 text-sm ${overdue ? "text-rose-600" : "text-amber-600"}`}>
                      <Calendar className="w-4 h-4 shrink-0" />
                      <span className="text-gray-500 text-xs w-16 shrink-0">Follow-up</span>
                      <span className="font-medium">{fmtDate(lead.nextFollowUpAt)}{overdue ? " (overdue)" : ""}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Timeline</div>
                  <div className="space-y-2">
                    {[
                      { label: "Lead Created",   date: lead.createdAt,       icon: User,          color: "text-blue-500"    },
                      { label: "Demo Confirmed", date: lead.confirmedAt,     icon: CheckCircle2,  color: "text-emerald-500" },
                      { label: "Last Contacted", date: lead.lastContactedAt, icon: Phone,         color: "text-violet-500"  },
                      { label: "Converted",      date: lead.convertedAt,     icon: TrendingUp,    color: "text-amber-500"   },
                      { label: "Expires",        date: lead.expiresAt,       icon: Clock,         color: "text-rose-500"    },
                    ].filter(t => t.date).map(t => (
                      <div key={t.label} className="flex items-center gap-3">
                        <t.icon className={`w-3.5 h-3.5 ${t.color} shrink-0`} />
                        <span className="text-gray-400 text-xs w-28 shrink-0">{t.label}</span>
                        <span className="text-gray-700 text-xs">{fmtDate(t.date)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Button size="sm" variant="outline" className="w-full rounded-xl text-gray-700 text-xs" onClick={() => setEditMode(true)}>
                  <Edit2 className="w-3.5 h-3.5 mr-1.5" /> Edit Details
                </Button>
              </>
            ) : (
              <form onSubmit={handleEditSubmit} className="space-y-3">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Edit Details</div>
                <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input name="name" defaultValue={lead.name} className="rounded-xl h-9 text-sm" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input name="phone" defaultValue={lead.phone} className="rounded-xl h-9 text-sm" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Country</Label><Input name="country" defaultValue={lead.country} className="rounded-xl h-9 text-sm" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Deal ($)</Label><Input name="dealValue" type="number" min="0" defaultValue={lead.dealValue ?? ""} className="rounded-xl h-9 text-sm" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Follow-up</Label><Input name="nextFollowUpAt" type="date" defaultValue={lead.nextFollowUpAt ? lead.nextFollowUpAt.slice(0, 10) : ""} className="rounded-xl h-9 text-sm" /></div>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Company</Label><Input name="companyName" defaultValue={lead.companyName} className="rounded-xl h-9 text-sm" /></div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Source</Label>
                  <Select name="source" defaultValue={lead.source}>
                    <SelectTrigger className="rounded-xl h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{SOURCES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Assigned To</Label><Input name="assignedTo" defaultValue={lead.assignedTo ?? ""} className="rounded-xl h-9 text-sm" placeholder="Name or email..." /></div>
                <div className="space-y-1.5"><Label className="text-xs">Website</Label><Input name="website" defaultValue={lead.website} className="rounded-xl h-9 text-sm" placeholder="https://" /></div>
                <div className="space-y-1.5"><Label className="text-xs">LinkedIn</Label><Input name="linkedinUrl" defaultValue={lead.linkedinUrl} className="rounded-xl h-9 text-sm" /></div>
                <div className="flex gap-2 pt-1">
                  <Button type="button" size="sm" variant="outline" className="flex-1 rounded-xl text-xs" onClick={() => setEditMode(false)}>Cancel</Button>
                  <Button type="submit" size="sm" className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs" disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                  </Button>
                </div>
              </form>
            )}

            <div className="pt-3 border-t border-gray-100">
              <Button size="sm" variant="ghost" className="w-full text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-xl text-xs" onClick={onDelete}>
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete Lead
              </Button>
            </div>
          </div>
        )}

        {activeTab === "notes" && (
          <NotesTab lead={lead} onSave={handleSaveNotes} saving={saving} />
        )}
      </div>
    </div>
  );
}

function NotesTab({ lead, onSave, saving }: { lead: Lead; onSave: (notes: string) => Promise<void>; saving: boolean }) {
  const [noteText, setNoteText] = useState(lead.notes || "");
  const dirty = noteText !== (lead.notes || "");
  return (
    <div className="px-6 py-5 space-y-3">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
        <MessageSquare className="w-3.5 h-3.5" /> Internal Notes
      </div>
      <Textarea
        value={noteText}
        onChange={e => setNoteText(e.target.value)}
        className="rounded-xl resize-none text-sm"
        rows={10}
        placeholder="Add notes about this lead, conversations, requirements, budget, timeline..."
      />
      <Button
        size="sm"
        className={`w-full rounded-xl text-xs ${dirty ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-gray-100 text-gray-400 cursor-not-allowed"}`}
        onClick={() => dirty && onSave(noteText)}
        disabled={saving || !dirty}
      >
        {saving ? "Saving..." : dirty ? "Save Notes" : "No Changes"}
      </Button>
      {lead.updatedAt && <p className="text-xs text-center text-gray-400">Last updated {fmtDateTime(lead.updatedAt)}</p>}
    </div>
  );
}

export default function CRMLeads() {
  const qc = useQueryClient();
  const [view, setView] = useState<"kanban" | "list">("kanban");
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState<Lead | null>(null);

  const { data: rawLeads = [], isLoading } = useQuery<Lead[]>({
    queryKey: ["cp-leads"],
    queryFn: () => apiGet("/cp/leads"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      apiPatch(`/cp/leads/${id}`, patch),
    onSuccess: (updated) => {
      qc.setQueryData<Lead[]>(["cp-leads"], prev =>
        prev ? prev.map(l => l.id === updated.id ? updated : l) : prev
      );
      if (selected?.id === updated.id) setSelected(updated);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/cp/leads/${id}`),
    onSuccess: (_, id) => {
      trackLeadDeleted();
      qc.setQueryData<Lead[]>(["cp-leads"], prev => prev?.filter(l => l.id !== id));
      setSelected(null);
    },
  });

  const leads = useMemo(() => {
    return rawLeads.filter(l => {
      const q = search.toLowerCase();
      const matchSearch = !q || l.email.toLowerCase().includes(q) || l.name?.toLowerCase().includes(q) || l.companyName?.toLowerCase().includes(q);
      const matchStage = stageFilter === "all" || l.stage === stageFilter;
      const matchSource = sourceFilter === "all" || l.source === sourceFilter;
      const matchPriority = priorityFilter === "all" || (l.priority ?? "normal") === priorityFilter;
      return matchSearch && matchStage && matchSource && matchPriority;
    });
  }, [rawLeads, search, stageFilter, sourceFilter, priorityFilter]);

  const stats = useMemo(() => {
    const total = rawLeads.length;
    const thisWeek = rawLeads.filter(l => new Date(l.createdAt) > new Date(Date.now() - 7 * 86400000)).length;
    const pipelineLeads = rawLeads.filter(l => PIPELINE_STAGES.includes(l.stage));
    const pipelineValue = pipelineLeads.reduce((sum, l) => sum + (l.dealValue ?? 0), 0);
    const won = rawLeads.filter(l => l.stage === "won").length;
    const lost = rawLeads.filter(l => l.stage === "lost").length;
    const convRate = (won + lost) > 0 ? Math.round((won / (won + lost)) * 100) : 0;
    const overdue = rawLeads.filter(l => isOverdue(l.nextFollowUpAt) && l.nextFollowUpAt).length;
    const highPriority = rawLeads.filter(l => l.priority === "high").length;
    const attention = rawLeads.filter(needsAttention).length;
    const hot = rawLeads.filter(l => leadScore(l, 0).label === "Hot" && PIPELINE_STAGES.includes(l.stage)).length;
    const sourceMap: Record<string, { count: number; won: number; value: number }> = {};
    for (const l of rawLeads) {
      const src = l.source || "other";
      if (!sourceMap[src]) sourceMap[src] = { count: 0, won: 0, value: 0 };
      sourceMap[src].count++;
      if (l.stage === "won") sourceMap[src].won++;
      sourceMap[src].value += l.dealValue ?? 0;
    }
    const sourcesBreakdown = Object.entries(sourceMap)
      .map(([id, v]) => ({ id, label: SOURCES.find(s => s.id === id)?.label ?? id, ...v, winRate: v.count > 0 ? Math.round((v.won / v.count) * 100) : 0 }))
      .sort((a, b) => b.count - a.count);
    return { total, thisWeek, pipelineValue, won, lost, convRate, pipelineLeads: pipelineLeads.length, overdue, highPriority, attention, hot, sourcesBreakdown };
  }, [rawLeads]);

  useEffect(() => {
    if (rawLeads.length > 0) {
      const pipelineValue = rawLeads
        .filter(l => PIPELINE_STAGES.includes(l.stage))
        .reduce((sum, l) => sum + (l.dealValue ?? 0), 0);
      trackLeadPipeline(rawLeads.length, pipelineValue);
    }
  }, [rawLeads.length]);

  const kanbanColumns = useMemo(() => {
    return STAGES.map(s => ({
      ...s,
      leads: leads.filter(l => l.stage === s.id),
      totalValue: leads.filter(l => l.stage === s.id).reduce((sum, l) => sum + (l.dealValue ?? 0), 0),
    }));
  }, [leads]);

  const handleUpdate = async (patch: Record<string, unknown>) => {
    if (!selected) return;
    await updateMutation.mutateAsync({ id: selected.id, patch });
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!confirm(`Delete lead "${selected.name || selected.email}"?`)) return;
    await deleteMutation.mutateAsync(selected.id);
  };

  const handleStageChange = (id: string, stage: string) => {
    updateMutation.mutate({ id, patch: { stage } });
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">CRM — Lead Pipeline</h2>
          <p className="text-sm text-gray-500 mt-1">
            {rawLeads.length} leads · {stats.pipelineLeads} in pipeline · {stats.won} won
            {stats.overdue > 0 && <span className="text-rose-600 ml-2">· {stats.overdue} overdue</span>}
          </p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700 rounded-xl text-white shadow-lg shadow-blue-500/20" onClick={() => setAddOpen(true)}>
          <Plus className="w-4 h-4 mr-2" /> Add Lead
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard label="Total Leads"    value={stats.total}        sub={`${stats.thisWeek} this week`} icon={User}         color="bg-blue-600" />
        <StatCard label="Pipeline Value" value={fmtCurrency(stats.pipelineValue)} sub={`${stats.pipelineLeads} active`} icon={DollarSign} color="bg-amber-600" />
        <StatCard label="Win Rate"       value={`${stats.convRate}%`} sub={`${stats.won} won · ${stats.lost} lost`} icon={TrendingUp} color="bg-emerald-600" />
        <StatCard
          label={stats.attention > 0 ? "Needs Attention" : stats.hot > 0 ? "Hot Leads" : "High Priority"}
          value={stats.attention > 0 ? stats.attention : stats.hot > 0 ? stats.hot : stats.highPriority}
          sub={stats.attention > 0 ? "Overdue or no contact 7d+" : stats.hot > 0 ? "High score in pipeline" : "Flagged leads"}
          icon={stats.attention > 0 ? AlertTriangle : stats.hot > 0 ? Flame : Flag}
          color={stats.attention > 0 ? "bg-rose-600" : stats.hot > 0 ? "bg-orange-500" : "bg-violet-600"}
          onClick={() => { if (stats.attention > 0) setPriorityFilter("all"); }}
        />
      </div>

      {/* Lead Source Attribution */}
      {stats.sourcesBreakdown.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-violet-600 flex items-center justify-center">
                <Tag className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-sm font-semibold text-gray-900">Lead Source Attribution</span>
            </div>
            <span className="text-xs text-gray-400">{rawLeads.length} total leads</span>
          </div>
          <div className="space-y-3">
            {stats.sourcesBreakdown.map(src => {
              const pct = stats.total > 0 ? Math.round((src.count / stats.total) * 100) : 0;
              const SOURCE_COLORS: Record<string, string> = {
                demo_request: "bg-blue-500", manual: "bg-slate-500", referral: "bg-emerald-500",
                website: "bg-violet-500", linkedin: "bg-sky-500", email: "bg-amber-500", other: "bg-gray-400",
              };
              const barColor = SOURCE_COLORS[src.id] ?? "bg-gray-400";
              return (
                <div key={src.id}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${barColor}`} />
                      <button className="text-xs font-medium text-gray-700 hover:text-blue-700 transition-colors"
                        onClick={() => setSourceFilter(src.id === sourceFilter ? "all" : src.id)}>
                        {src.label}
                      </button>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      {src.winRate > 0 && <span className="text-emerald-600 font-medium">{src.winRate}% WR</span>}
                      {src.value > 0 && <span>${(src.value / 1000).toFixed(0)}K</span>}
                      <span className="font-semibold text-gray-900">{src.count} <span className="font-normal text-gray-400">({pct}%)</span></span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${barColor} transition-all duration-500`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, company..." className="pl-9 rounded-xl text-sm h-9" />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="rounded-xl h-9 text-sm w-44">
            <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5 text-gray-400" /><SelectValue placeholder="All Stages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {STAGES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="rounded-xl h-9 text-sm w-40">
            <Globe className="w-3.5 h-3.5 mr-1.5 text-gray-400" /><SelectValue placeholder="All Sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {SOURCES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="rounded-xl h-9 text-sm w-36">
            <Flag className="w-3.5 h-3.5 mr-1.5 text-gray-400" /><SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priority</SelectItem>
            {PRIORITIES.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 border border-gray-200">
          <button onClick={() => setView("kanban")} className={`p-1.5 rounded-lg transition-colors ${view === "kanban" ? "bg-blue-600 text-white" : "text-gray-500 hover:text-gray-800"}`}>
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button onClick={() => setView("list")} className={`p-1.5 rounded-lg transition-colors ${view === "list" ? "bg-blue-600 text-white" : "text-gray-500 hover:text-gray-800"}`}>
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {isLoading && <div className="text-center py-16 text-gray-400">Loading leads...</div>}

      {!isLoading && view === "kanban" && (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {kanbanColumns.map(col => (
              <div key={col.id} className="w-64 shrink-0">
                <div className="flex items-center gap-2 mb-3 px-1">
                  <span className={`w-2 h-2 rounded-full ${col.dot}`} />
                  <span className="text-xs font-semibold text-gray-700">{col.label}</span>
                  <span className="ml-auto text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">{col.leads.length}</span>
                </div>
                {col.totalValue > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-emerald-600 mb-2 px-1">
                    <DollarSign className="w-3 h-3" />
                    {fmtCurrency(col.totalValue)}
                  </div>
                )}
                <div className="space-y-3 min-h-[120px]">
                  {col.leads.map(lead => (
                    <LeadCard key={lead.id} lead={lead} onClick={() => setSelected(lead)} onStageChange={handleStageChange} />
                  ))}
                  {col.leads.length === 0 && (
                    <div className="border border-dashed border-gray-200 rounded-xl p-4 text-center text-xs text-gray-400">
                      Drop leads here
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!isLoading && view === "list" && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="text-xs font-semibold text-gray-500">Lead</TableHead>
                <TableHead className="text-xs font-semibold text-gray-500">Company</TableHead>
                <TableHead className="text-xs font-semibold text-gray-500">Stage</TableHead>
                <TableHead className="text-xs font-semibold text-gray-500">Priority</TableHead>
                <TableHead className="text-xs font-semibold text-gray-500">Source</TableHead>
                <TableHead className="text-xs font-semibold text-gray-500">Deal</TableHead>
                <TableHead className="text-xs font-semibold text-gray-500">Follow-up</TableHead>
                <TableHead className="text-xs font-semibold text-gray-500">Created</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-12 text-gray-400">No leads match your filters.</TableCell></TableRow>
              )}
              {leads.map(lead => {
                const overdue = isOverdue(lead.nextFollowUpAt);
                return (
                  <TableRow key={lead.id} className="cursor-pointer hover:bg-gray-50 transition-colors group" onClick={() => setSelected(lead)}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-full ${getStage(lead.stage).color} flex items-center justify-center text-white text-xs font-bold shrink-0`}>
                          {avatar(lead)}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{lead.name || lead.email}</div>
                          {lead.name && <div className="text-xs text-gray-400">{lead.email}</div>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">{lead.companyName || "—"}</TableCell>
                    <TableCell><StageBadge stage={lead.stage} /></TableCell>
                    <TableCell>
                      {lead.priority === "high" && <span className="text-xs text-rose-600 flex items-center gap-1"><Flag className="w-3 h-3" /> High</span>}
                      {lead.priority === "low"  && <span className="text-xs text-gray-400">Low</span>}
                      {(!lead.priority || lead.priority === "normal") && <span className="text-xs text-gray-400">Normal</span>}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">{getSource(lead.source)}</TableCell>
                    <TableCell className="text-sm font-medium text-emerald-700">{fmtCurrency(lead.dealValue)}</TableCell>
                    <TableCell>
                      {lead.nextFollowUpAt ? (
                        <span className={`text-xs ${overdue ? "text-rose-600 font-medium" : "text-gray-600"} flex items-center gap-1`}>
                          {overdue && <AlertTriangle className="w-3 h-3" />}
                          {fmtDate(lead.nextFollowUpAt)}
                        </span>
                      ) : <span className="text-xs text-gray-300">—</span>}
                    </TableCell>
                    <TableCell className="text-xs text-gray-400">{fmtDate(lead.createdAt)}</TableCell>
                    <TableCell>
                      <ChevronRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <AddLeadModal open={addOpen} onClose={() => setAddOpen(false)} />

      {selected && (
        <>
          <div className="fixed inset-0 bg-black/20 z-40" onClick={() => setSelected(null)} />
          <LeadDetailPanel
            lead={selected}
            onClose={() => setSelected(null)}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
          />
        </>
      )}
    </div>
  );
}
